import { Ban, Banknote, Eye, Paperclip, Plus, ReceiptText, Trash2, WalletCards, X } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import {
  invoiceBalance,
  invoicePaidAmount,
  quoteTotal,
  type CompanyProfile,
  type Invoice,
  type Payment,
  type PaymentAttachment,
  type PaymentMethod,
  type Plan,
} from '../types';
import { BillingDocumentPreview } from './BillingDocumentPreview';
import { AttachmentPreview } from './AttachmentPreview';
import {thaiDate} from '../../shared/thaiDate';

interface InvoiceManagerProps {
  invoice: Invoice;
  payments: Payment[];
  company: CompanyProfile;
  plan: Plan;
  onClose: () => void;
  onAddPayment: (draft: Pick<Payment, 'paidAt' | 'amount' | 'method' | 'reference' | 'note' | 'attachment'>) => Promise<void>;
  onVoidPayment: (payment: Payment) => Promise<void>;
  onArchive: () => Promise<void>;
  onUpdateDueDate: (dueDate: string) => Promise<void>;
}

const money = (value: number) => `฿${value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (value: string) => new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium' }).format(new Date(value));
const methodLabels: Record<PaymentMethod, string> = {
  bank_transfer: 'โอนเงิน',
  cash: 'เงินสด',
  cheque: 'เช็ค',
  other: 'อื่น ๆ',
};

const readAttachment = (file: File | undefined): Promise<PaymentAttachment | null> => {
  if (!file) return Promise.resolve(null);
  const allowed = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
  if (!allowed.includes(file.type)) return Promise.reject(new Error('รองรับเฉพาะ PNG, JPG, WebP หรือ PDF'));
  if (file.size > 5 * 1024 * 1024) return Promise.reject(new Error('หลักฐานต้องมีขนาดไม่เกิน 5 MB'));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, size: file.size, dataUrl: String(reader.result || '') });
    reader.onerror = () => reject(new Error('อ่านไฟล์หลักฐานไม่สำเร็จ'));
    reader.readAsDataURL(file);
  });
};

export function InvoiceManager({ invoice, payments, company, plan, onClose, onAddPayment, onVoidPayment, onArchive, onUpdateDueDate }: InvoiceManagerProps) {
  const activePayments = useMemo(() => payments.filter((payment) => payment.invoiceId === invoice.id && !payment.voidedAt), [invoice.id, payments]);
  const history = useMemo(() => payments.filter((payment) => payment.invoiceId === invoice.id), [invoice.id, payments]);
  const latestPayment = useMemo(() => [...activePayments].sort((left, right) => right.createdAt.localeCompare(left.createdAt))[0], [activePayments]);
  const paid = invoicePaidAmount(invoice.id, payments);
  const balance = invoiceBalance(invoice, payments);
  const [paidAt, setPaidAt] = useState(()=>thaiDate());
  const [amount, setAmount] = useState(String(balance || ''));
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ type: 'invoice' } | { type: 'receipt'; payment: Payment } | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<PaymentAttachment | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) { setError('กรุณากรอกยอดรับชำระให้ถูกต้อง'); return; }
    if (numericAmount > balance + 0.001) { setError(`ยอดรับชำระเกินยอดคงเหลือ ${money(balance)}`); return; }
    setBusy(true);
    setError('');
    try {
      const attachment = await readAttachment(attachmentFile);
      await onAddPayment({ paidAt, amount: numericAmount, method, reference: reference.trim(), note: note.trim(), attachment });
      setAmount('');
      setReference('');
      setNote('');
      setAttachmentFile(undefined);
      const input = document.getElementById('payment-attachment') as HTMLInputElement | null;
      if (input) input.value = '';
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'บันทึกรับชำระไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return <>
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="modal payment-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <div><span className="eyebrow">ใบแจ้งหนี้และการรับชำระ</span><h2>{invoice.number}</h2></div>
          <div className="header-actions"><button className="btn ghost" type="button" onClick={() => setPreview({ type: 'invoice' })}><Eye size={17} /> ดูใบแจ้งหนี้</button>{latestPayment && <button className="btn ghost receipt-header-button" type="button" onClick={() => setPreview({ type: 'receipt', payment: latestPayment })}><ReceiptText size={17} /> ดูใบเสร็จล่าสุด</button>}<button className="icon-btn danger" type="button" onClick={() => void onArchive()} disabled={activePayments.length > 0} title={activePayments.length > 0 ? 'ต้องยกเลิกรายการรับเงินก่อนนำใบแจ้งหนี้ออก' : 'นำใบแจ้งหนี้ออก'}><Trash2 size={17} /></button><button className="icon-btn" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button></div>
        </header>

        <div className="payment-content">
          <section className="invoice-overview">
            <div><span>ลูกค้า</span><strong>{invoice.quoteSnapshot.customerName}</strong><small>อ้างอิง {invoice.quoteSnapshot.number}</small></div>
            <div><span>ยอดใบแจ้งหนี้</span><strong>{money(quoteTotal(invoice.quoteSnapshot))}</strong><label className="due-date-control"><span>ครบกำหนด</span><input type="date" value={invoice.dueDate} min={invoice.issueDate} onChange={(event) => void onUpdateDueDate(event.target.value)} /></label></div>
            <div><span>รับแล้ว</span><strong className="received-value">{money(paid)}</strong><small>{activePayments.length} งวด</small></div>
            <div><span>คงเหลือ</span><strong className={balance > 0 ? 'balance-value' : 'received-value'}>{money(balance)}</strong><small>{balance === 0 ? 'ชำระครบแล้ว' : 'รอรับชำระ'}</small></div>
          </section>

          <div className="payment-layout">
            <section className="payment-history">
              <div className="section-heading"><div><span className="section-kicker">PAYMENT HISTORY</span><h3>ประวัติการรับชำระ</h3></div><span>{history.length} รายการ</span></div>
              {history.length === 0 ? <div className="payment-empty"><WalletCards size={26} /><strong>ยังไม่มีรายการรับชำระ</strong><p>เพิ่มยอดที่ได้รับจริงจากแบบฟอร์มด้านขวา</p></div> : <div className="payment-timeline">{history.map((payment) => <article className={`payment-entry ${payment.voidedAt ? 'voided' : ''}`} key={payment.id}>
                <div className="payment-entry-icon">{payment.voidedAt ? <Ban size={17} /> : <Banknote size={17} />}</div>
                <div className="payment-entry-main"><div><strong>{money(payment.amount)}</strong><span>{methodLabels[payment.method]} · {date(payment.paidAt)}</span></div><small>{payment.voidedAt ? `ยกเลิกเมื่อ ${date(payment.voidedAt)}` : payment.receiptNumber}{payment.reference ? ` · อ้างอิง ${payment.reference}` : ''}</small>{payment.note && <p>{payment.note}</p>}
                  <div className="payment-entry-actions">{payment.attachment && <button type="button" className="text-link" onClick={() => setAttachmentPreview(payment.attachment!)}><Paperclip size={14} /> ดูหลักฐาน</button>}{!payment.voidedAt && <button type="button" className="text-link" onClick={() => setPreview({ type: 'receipt', payment })}><ReceiptText size={14} /> ดูใบเสร็จ</button>}{!payment.voidedAt && <button type="button" className="text-link danger-link" onClick={() => void onVoidPayment(payment)}><Ban size={14} /> ยกเลิกรายการ</button>}</div>
                </div>
              </article>)}</div>}
            </section>

            <section className="payment-form-card">
              <div className="section-heading"><div><span className="section-kicker">MANUAL PAYMENT</span><h3>บันทึกรับชำระ</h3></div><Plus size={19} /></div>
              {balance === 0 ? <div className="paid-complete"><ReceiptText size={25} /><strong>ใบแจ้งหนี้นี้ชำระครบแล้ว</strong><p>หากบันทึกผิด ให้ยกเลิกรายการเดิมก่อนเพิ่มรายการใหม่</p></div> : <form onSubmit={submit}>
                <div className="field-grid two"><label><span>วันที่รับเงิน</span><input type="date" value={paidAt} max={thaiDate()} onChange={(event) => setPaidAt(event.target.value)} required /></label><label><span>ช่องทาง</span><select value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}>{Object.entries(methodLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label></div>
                <label><span>ยอดที่ได้รับ</span><div className="money-input"><span>฿</span><input type="number" min="0.01" step="0.01" max={balance} value={amount} onChange={(event) => setAmount(event.target.value)} required /></div><small className="field-hint">รับได้สูงสุด {money(balance)}</small></label>
                <label><span>เลขอ้างอิง (ถ้ามี)</span><input value={reference} maxLength={80} onChange={(event) => setReference(event.target.value)} placeholder="เช่น เลขรายการโอน / เลขเช็ค" /></label>
                <label><span>หมายเหตุ</span><textarea rows={2} value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} /></label>
                <label><span>หลักฐานการชำระ</span><input id="payment-attachment" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(event) => setAttachmentFile(event.target.files?.[0])} /><small className="field-hint">PNG, JPG, WebP หรือ PDF ไม่เกิน 5 MB · เก็บในอุปกรณ์นี้</small></label>
                {error && <p className="form-error" role="alert">{error}</p>}
                <button className="btn primary payment-submit" type="submit" disabled={busy}><Plus size={17} /> {busy ? 'กำลังบันทึก…' : 'บันทึกรับชำระ'}</button>
              </form>}
            </section>
          </div>
        </div>
      </section>
    </div>
    {preview && <BillingDocumentPreview invoice={invoice} payments={payments} company={company} plan={plan} payment={preview.type === 'receipt' ? preview.payment : undefined} onClose={() => setPreview(null)} />}
    {attachmentPreview && <AttachmentPreview attachment={attachmentPreview} label="หลักฐานการชำระ" onClose={() => setAttachmentPreview(null)} />}
  </>;
}
